/**
 * Держит экран включённым, пока идёт воспроизведение: Screen Wake Lock API.
 *
 * Зачем это плееру. iOS не сбрасывает таймер автоблокировки ради видео без
 * звука, а плеер стартует приглушённым: зритель смотрит канал, экран гаснет
 * через минуту, и в системном полноэкранном режиме это выглядит особенно
 * дико. Wake Lock просит систему не гасить экран явно, независимо от того,
 * есть ли у потока звук. Safari поддерживает его с 16.4.
 *
 * Правила API, из которых растут все ветки ниже: замок можно просить
 * только у видимой страницы и только в безопасном контексте; система сама
 * отпускает его, когда страница уходит в фон, поэтому при возвращении надо
 * просить заново; запрос может отклониться, например при низком заряде, и
 * это не ошибка, а отказ, который надо молча пережить.
 */
export default class ScreenWakeLock {
  sentinel = null;

  /** Хочет ли плеер держать экран прямо сейчас. */
  wanted = false;

  static isSupported() {
    return (
      typeof navigator !== "undefined" &&
      typeof document !== "undefined" &&
      "wakeLock" in navigator
    );
  }

  acquire() {
    if (!ScreenWakeLock.isSupported()) return;

    if (!this.wanted) {
      this.wanted = true;
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }

    this.request();
  }

  release() {
    if (!ScreenWakeLock.isSupported()) return;

    this.wanted = false;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);

    const { sentinel } = this;
    this.sentinel = null;

    if (sentinel && !sentinel.released) {
      sentinel.release().catch(() => {});
    }
  }

  async request() {
    if (this.sentinel && !this.sentinel.released) return;
    if (document.visibilityState !== "visible") return;

    try {
      const sentinel = await navigator.wakeLock.request("screen");

      // Пока ждали ответа, плеер мог остановиться: не держим лишнего.
      if (!this.wanted) {
        sentinel.release().catch(() => {});
        return;
      }

      sentinel.addEventListener("release", () => {
        if (this.sentinel === sentinel) {
          this.sentinel = null;
        }
      });

      this.sentinel = sentinel;
    } catch (error) {
      // NotAllowedError при низком заряде, режиме энергосбережения или
      // запрете в настройках: система решила, что экран гасить можно.
    }
  }

  onVisibilityChange = () => {
    if (this.wanted && document.visibilityState === "visible") {
      this.request();
    }
  };
}
